/**
 * Product 360 Viewer - Standalone Web Component
 * 
 * A modern, dependency-free web component for displaying interactive 360-degree product views.
 * 
 * @version 1.0.0
 * @author Allurez
 */

(function() {
    'use strict';

    // Prevent multiple registrations
    if (customElements.get('product-360-viewer')) {
        return;
    }

    class Product360Viewer extends HTMLElement {
        
        constructor() {
            super();
            
            // Component state
            this.isLoaded = false;
            this.isPlaying = false;
            this.isDragging = false;
            this.currentFrame = 0;
            this.totalFrames = 0;
            this.images = [];
            this.animationInterval = null;
            this.lastMouseX = 0;
            this.dragStartX = 0;
            
            // Configuration
            this.config = {
                autoPlay: true,
                frameRate: 30, // frames per second
                dragSensitivity: 2, // how many frames to advance per pixel dragged
                preloadImages: true
            };
        }

        /**
         * Get observed attributes
         */
        static get observedAttributes() {
            return ['vendor-sku', 'image-total-count', 'image-path', 'auto-play'];
        }

        /**
         * Handle attribute changes
         */
        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue !== newValue && this.isLoaded) {
                this.reload();
            }
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            this.cleanup();
        }

        /**
         * Initialize the component
         */
        init() {
            const vendorSku = this.getAttribute('vendor-sku');
            const imageTotalCount = this.getAttribute('image-total-count');
            const imagePath = 'https://media.allurez.com/';
            const autoPlay = this.getAttribute('auto-play') !== 'false';

            if (!vendorSku || !imageTotalCount) {
                console.error('product-360-viewer: vendor-sku and image-total-count attributes are required');
                return;
            }

            this.config.autoPlay = autoPlay;
            this.vendorSku = vendorSku;
            this.imagePath = imagePath;
            this.totalFrames = parseInt(imageTotalCount);

            this.render();
            this.setupEventListeners();
            this.preloadImages();
        }

        /**
         * Render the component HTML
         */
        render() {
            this.innerHTML = `
                <div class="product-360-viewer__container">
                    <!-- Loading Progress -->
                    <div class="product-360-viewer__progress-container">
                        <div class="product-360-viewer__progress-bar">
                            <div class="product-360-viewer__progress"></div>
                        </div>
                        <div class="product-360-viewer__progress-text">0%</div>
                    </div>
                    
                    <!-- Main Image -->
                    <img 
                        class="product-360-viewer__image" 
                        src="" 
                        alt="360 degree view"
                        width="400"
                        height="400"
                        loading="lazy"
                    >
                    
                    <!-- Controls -->
                    <div class="product-360-viewer__controls">
                        <button 
                            class="product-360-viewer__control-btn" 
                            type="button"
                            aria-label="Play 360 view"
                            title="Play 360 view"
                        >
                            ▶
                        </button>
                        <button 
                            class="product-360-viewer__control-btn" 
                            type="button"
                            aria-label="Pause 360 view"
                            title="Pause 360 view"
                        >
                            ⏸
                        </button>
                    </div>
                    
                    <!-- Frame Counter -->
                    <div class="product-360-viewer__frame-counter">
                        <span class="current-frame">1</span> / <span class="total-frames">${this.totalFrames}</span>
                    </div>
                </div>
            `;
        }

        /**
         * Setup event listeners
         */
        setupEventListeners() {
            const container = this.querySelector('.product-360-viewer__container');
            const image = this.querySelector('.product-360-viewer__image');
            const playBtn = this.querySelector('.product-360-viewer__control-btn:first-child');
            const pauseBtn = this.querySelector('.product-360-viewer__control-btn:last-child');

            if (!container || !image) return;

            // Mouse events
            container.addEventListener('mousedown', this.handleMouseDown.bind(this));
            container.addEventListener('mousemove', this.handleMouseMove.bind(this));
            container.addEventListener('mouseup', this.handleMouseUp.bind(this));
            container.addEventListener('mouseleave', this.handleMouseUp.bind(this));

            // Touch events
            container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
            container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
            container.addEventListener('touchend', this.handleTouchEnd.bind(this));

            // Control buttons
            if (playBtn) {
                playBtn.addEventListener('click', () => this.play());
            }
            if (pauseBtn) {
                pauseBtn.addEventListener('click', () => this.pause());
            }

            // Prevent context menu and drag
            container.addEventListener('contextmenu', (e) => e.preventDefault());
            container.addEventListener('dragstart', (e) => e.preventDefault());

            // Image load events
            image.addEventListener('load', this.handleImageLoad.bind(this));
            image.addEventListener('error', this.handleImageError.bind(this));
        }

        /**
         * Preload all images
         */
        async preloadImages() {
            const progressContainer = this.querySelector('.product-360-viewer__progress-container');
            const progressBar = this.querySelector('.product-360-viewer__progress');
            const progressText = this.querySelector('.product-360-viewer__progress-text');
            
            // Show progress container
            if (progressContainer) progressContainer.classList.add('show');
            if (progressText) progressText.textContent = 'Loading...';

            this.images = [];
            let loadedCount = 0;

            const loadPromises = [];
            
            for (let i = 1; i <= this.totalFrames; i++) {
                const frameNumber = i.toString().padStart(2, '0');
                const imageUrl = `${this.imagePath}${this.vendorSku}/${this.vendorSku}_${frameNumber}.jpg`;
                
                const loadPromise = this.loadImage(imageUrl).then(img => {
                    loadedCount++;
                    const progress = Math.round((loadedCount / this.totalFrames) * 100);
                    
                    if (progressBar) {
                        progressBar.style.width = `${progress}%`;
                    }
                    if (progressText) {
                        progressText.textContent = `${progress}%`;
                    }
                    
                    return img;
                }).catch(error => {
                    console.warn(`Failed to load image: ${imageUrl}`, error);
                    return null;
                });

                loadPromises.push(loadPromise);
            }

            try {
                this.images = await Promise.all(loadPromises);
                this.images = this.images.filter(img => img !== null);
                
                // Hide progress container
                if (progressContainer) progressContainer.classList.remove('show');
                
                this.isLoaded = true;
                this.showFirstFrame();
                
                if (this.config.autoPlay) {
                    this.startAutoPlay();
                }
                
                // Dispatch custom event
                this.dispatchEvent(new CustomEvent('imagesLoaded', {
                    detail: { totalImages: this.images.length }
                }));
                
            } catch (error) {
                console.error('Error preloading images:', error);
                this.showError('Failed to load images');
            }
        }

        /**
         * Load a single image
         */
        loadImage(src) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = src;
            });
        }

        /**
         * Show the first frame
         */
        showFirstFrame() {
            if (this.images.length > 0) {
                this.currentFrame = 0;
                this.updateImage();
            }
        }

        /**
         * Start auto-play animation
         */
        startAutoPlay() {
            if (this.isPlaying || this.images.length === 0) return;
            
            this.isPlaying = true;
            const frameDelay = 1000 / this.config.frameRate;
            
            this.animationInterval = setInterval(() => {
                this.currentFrame = (this.currentFrame + 1) % this.images.length;
                this.updateImage();
            }, frameDelay);
        }

        /**
         * Stop auto-play animation
         */
        stopAutoPlay() {
            this.isPlaying = false;
            if (this.animationInterval) {
                clearInterval(this.animationInterval);
                this.animationInterval = null;
            }
        }

        /**
         * Update the displayed image
         */
        updateImage() {
            const image = this.querySelector('.product-360-viewer__image');
            const currentFrameSpan = this.querySelector('.current-frame');
            
            if (image && this.images[this.currentFrame]) {
                image.src = this.images[this.currentFrame].src;
            }
            
            if (currentFrameSpan) {
                currentFrameSpan.textContent = this.currentFrame + 1;
            }

            // Dispatch custom event
            this.dispatchEvent(new CustomEvent('frameChanged', {
                detail: { currentFrame: this.currentFrame }
            }));
        }

        /**
         * Handle mouse down
         */
        handleMouseDown(event) {
            event.preventDefault();
            this.isDragging = true;
            this.dragStartX = event.clientX;
            this.lastMouseX = event.clientX;
            this.stopAutoPlay();
        }

        /**
         * Handle mouse move
         */
        handleMouseMove(event) {
            if (!this.isDragging) return;
            
            event.preventDefault();
            const deltaX = event.clientX - this.lastMouseX;
            const frameDelta = Math.round(deltaX / this.config.dragSensitivity);
            
            if (frameDelta !== 0) {
                this.currentFrame = (this.currentFrame + frameDelta + this.images.length) % this.images.length;
                this.updateImage();
                this.lastMouseX = event.clientX;
            }
        }

        /**
         * Handle mouse up
         */
        handleMouseUp(event) {
            if (!this.isDragging) return;
            
            this.isDragging = false;
            
            // Resume auto-play after a delay if it was enabled
            if (this.config.autoPlay) {
                setTimeout(() => {
                    if (!this.isDragging) {
                        this.startAutoPlay();
                    }
                }, 1000);
            }
        }

        /**
         * Handle touch start
         */
        handleTouchStart(event) {
            event.preventDefault();
            const touch = event.touches[0];
            this.isDragging = true;
            this.dragStartX = touch.clientX;
            this.lastMouseX = touch.clientX;
            this.stopAutoPlay();
        }

        /**
         * Handle touch move
         */
        handleTouchMove(event) {
            if (!this.isDragging) return;
            
            event.preventDefault();
            const touch = event.touches[0];
            const deltaX = touch.clientX - this.lastMouseX;
            const frameDelta = Math.round(deltaX / this.config.dragSensitivity);
            
            if (frameDelta !== 0) {
                this.currentFrame = (this.currentFrame + frameDelta + this.images.length) % this.images.length;
                this.updateImage();
                this.lastMouseX = touch.clientX;
            }
        }

        /**
         * Handle touch end
         */
        handleTouchEnd(event) {
            if (!this.isDragging) return;
            
            this.isDragging = false;
            
            // Resume auto-play after a delay if it was enabled
            if (this.config.autoPlay) {
                setTimeout(() => {
                    if (!this.isDragging) {
                        this.startAutoPlay();
                    }
                }, 1000);
            }
        }

        /**
         * Handle image load
         */
        handleImageLoad() {
            // Image loaded successfully
        }

        /**
         * Handle image error
         */
        handleImageError() {
            console.warn('Failed to load image in 360 viewer');
        }

        /**
         * Show error message
         */
        showError(message) {
            const container = this.querySelector('.product-360-viewer__container');
            if (container) {
                container.innerHTML = `<div class="product-360-viewer__error">${message}</div>`;
            }
        }

        /**
         * Reload the component
         */
        reload() {
            this.cleanup();
            this.init();
        }

        /**
         * Cleanup resources
         */
        cleanup() {
            this.stopAutoPlay();
            this.isDragging = false;
            this.isLoaded = false;
            this.images = [];
        }

        /**
         * Public API methods
         */
        
        /**
         * Play the animation
         */
        play() {
            this.startAutoPlay();
        }

        /**
         * Pause the animation
         */
        pause() {
            this.stopAutoPlay();
        }

        /**
         * Go to specific frame
         */
        goToFrame(frameNumber) {
            if (frameNumber >= 0 && frameNumber < this.images.length) {
                this.currentFrame = frameNumber;
                this.updateImage();
            }
        }

        /**
         * Get current frame number
         */
        getCurrentFrame() {
            return this.currentFrame;
        }

        /**
         * Get total frames
         */
        getTotalFrames() {
            return this.images.length;
        }

        /**
         * Set configuration
         */
        setConfig(newConfig) {
            this.config = { ...this.config, ...newConfig };
        }
    }

    // Register the custom element
    customElements.define('product-360-viewer', Product360Viewer);

    // Export for module systems if available
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = Product360Viewer;
    }
    
    if (typeof window !== 'undefined') {
        window.Product360Viewer = Product360Viewer;
    }

})();
